import dayjs from "dayjs";
import { Category } from "./types";
import { PROP_NAME_SORTBY } from "./constants";
import { SetURLSearchParams } from "react-router-dom";

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const initSearchData = { searchCity: [""], searchStatus: [""] };
export const initSearchDispute = { city: [""], insurance: [""] };

export const getQueryFromArray = (array: Array<string | number>, queryName: string) => {
  return array.reduce((all, next) => all + `&${queryName}=${next}`, "");
};

export const highlightText = (text: string, query: string, className: string): React.ReactNode => {
  if (!query) return text;

  const regex = new RegExp(query, "gi");
  let match;
  let lastIndex = 0;
  const highlightedParts: React.ReactNode[] = [];

  while ((match = regex.exec(text)) !== null) {
    highlightedParts.push(text.substring(lastIndex, match.index));
    highlightedParts.push(
      <span className={className} key={match.index}>
        {match[0]}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }
  highlightedParts.push(text.substring(lastIndex));
  return highlightedParts;
};

export const getTreeItems = (category: Category) => {
  let children: any[] = [];

  if (category.children.length && category.productCategories.length) {
    children = category.children?.map((el) => getTreeItems(el));
  }
  if (category.productCategories.length) {
    const otherChildren = category.productCategories?.map((el) => ({
      ...el,
      value: el.id,
      label: el.name,
      isProduct: true,
      children: [],
    }));

    if (children?.length) {
      children = children.concat(otherChildren);
    } else {
      children = otherChildren;
    }
  }

  if (category.children.length && !category.productCategories.length) {
    children = category.children?.map((el) => getTreeItems(el));
  }

  return {
    value: category.id,
    id: category.id,
    order: category.order,
    language: category.languageCode,
    key: category.id,
    label: category.name,
    isProduct: false,
    children,
  };
};

export const extractProductCategories = (dataArray) => {
  const result = new Set();

  function traverse(node) {
    if (node.productCategories && Array.isArray(node.productCategories)) {
      node.productCategories.forEach((category) => {
        result.add(JSON.stringify({ id: category.id, name: category.name }));
      });
    }

    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child) => traverse(child));
    }
  }

  dataArray.forEach((data) => traverse(data));

  return Array.from(result).map((item: string) => JSON.parse(item));
};

const DAY_MILLISECONDS = 86400000;
export const getDays = (from: string, to: string) => {
  const days: string[] = [];
  const fromMilliseconds = +new Date(from);
  const toMilliseconds = +new Date(to);

  let dayInMilliseconds = fromMilliseconds;
  while (dayInMilliseconds <= toMilliseconds) {
    days.push(dayjs(new Date(dayInMilliseconds)).format("YYYY-MM-DD"));

    dayInMilliseconds += DAY_MILLISECONDS;
  }

  return days;
};

export const formatDate = (date: string, options: Intl.DateTimeFormatOptions) =>
  new Date(date).toLocaleDateString("en-GB", options).replaceAll("/", ".");
export const trimEndDate = (endDate: string) => endDate.replace("T23:59:59.999", "");
const monthDayOptions: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
// backend dates are UTC calendar days (end is "T23:59:59.999Z"), so count in UTC, not local time
const getUtcDayIndex = (date: string | number | Date) => Math.floor(+new Date(date) / DAY_MILLISECONDS);
// inclusive count of calendar days: "19 Oct - 20 Oct" is 2 days
export const getDaysCount = (startDate: string | number | Date, endDate: string | number | Date) =>
  getUtcDayIndex(endDate) - getUtcDayIndex(startDate) + 1;

export const formatDaysCount = (daysCount: number) => `${daysCount} day${daysCount > 1 ? "s" : ""}`;

export const getPeriod = (startDate: string, endDate: string) => {
  const start = formatDate(startDate, monthDayOptions);
  const end = formatDate(trimEndDate(endDate), monthDayOptions);

  return `${start} - ${end}, \n ${formatDaysCount(getDaysCount(startDate, trimEndDate(endDate)))}`;
};

export function formatDateRange(startDate: Date, endDate: Date): string {
  if (!(startDate instanceof Date && endDate instanceof Date) || startDate > endDate) {
    console.error(`Invalid date input. Start date: ${startDate}. End date: ${endDate}`);
    return "Invalid Date";
  }
  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", timeZone: "UTC" };
  const formattedStartDate = startDate.toLocaleDateString("en-US", options);
  const formattedEndDate = endDate.toLocaleDateString("en-US", options);

  return `${formattedStartDate} – ${formattedEndDate}`;
}

export const onSort = (
  sortName: string,
  searchParams: URLSearchParams,
  sortBy: string,
  setSearchParams: SetURLSearchParams,
) => {
  const newSearchParams = new URLSearchParams(searchParams);
  if (sortBy === sortName) {
    newSearchParams.delete(PROP_NAME_SORTBY);
  } else {
    newSearchParams.set(PROP_NAME_SORTBY, sortName);
  }
  setSearchParams(newSearchParams, { replace: true });
};

export function removePostalIndexCountryCity(address: string): string {
  // Современный вариант с lookbehind (поддерживается в актуальных браузерах/Node)
  const withoutIndex = address.replace(/(?<!\d)\d{5,6}(?!\d)/g, "");

  // Приводим пробелы/знаки препинания в порядок
  const withoutIdx = withoutIndex
    .replace(/\s{2,}/g, " ") // сдваивающиеся пробелы -> один
    .replace(/\s+,/g, ",") // пробел перед запятой -> убираем
    .replace(/\s+-\s+/g, "-") // пробелы вокруг дефиса -> убираем
    .replace(/,\s*,+/g, ",") // двойные запятые -> одна
    .replace(/^\s+|\s+$/g, ""); // обрезаем по краям

  const withoutComma = withoutIdx[withoutIdx.length - 1] === "," ? withoutIdx.slice(0, -1) : withoutIdx;

  const splited = withoutComma.split(",");

  return splited.join(", ");
}
export const INIT_CHATS = {
  debateChat: { cursor: 0, messages: [] },
  selected: null,
  supportChat: { cursor: 0, messages: [] },
};

export function formatPhoneAny(input: string) {
  // 1. Убираем ВСЁ кроме цифр
  const clean = input.replace(/\D/g, "");

  // 2. Группируем по 3 с конца (стандарт для телефонов)
  let formatted = "";
  for (let i = clean.length; i > 0; i -= 3) {
    formatted = (i > 3 ? " " : "") + clean.slice(Math.max(0, i - 3), i) + formatted;
  }

  return formatted;
}

export const getOptions = (option: any) => {
  const res = {
    name: option.option.name,
    value: "",
    id: option.option.id,
  };
  if (option.value) {
    res.value = option.value;
  } else {
    res.value = option.selectValues.map((val) => val.value).join(", ");
  }
  return res;
};

export { formatNumber, formatMoney, formatPercent } from "./format";
